import { ReactElement, Suspense, SyntheticEvent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import {
  EContainerIconSize,
  EIconSize,
  SkeletonAvatarSize
} from 'utils/constants/app/ui';
import { MATCH_TRACKER_AVAILABLE_SPORTS } from 'utils/constants/sportsbook/match-tracker-sports';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import { checkIsNotESportType } from 'utils/sportsbook/check-type-is-not-sportbook';
import { ETeamNumber } from 'interfaces/sportsbook';
import { Game, IGameStats } from 'interfaces/sportsbook-data-levels';
import { AFTER_SIGN_IN_ACTION_TYPES } from 'services/afterSignIn';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { EventAction } from 'components/EventAction';
import { GlobalIcon } from 'components/GlobalIcon';
import { TeamLogo } from 'components/TeamLogo';
import { MatchStatistic } from 'newcomponents/Shared/sportsbook/MatchStatistic';
import { NotificationIcon } from 'newcomponents/Shared/sportsbook/NotificationIcon';
import { VideoCamIcon } from 'newcomponents/Shared/sportsbook/VideoCamIcon';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useOpenBetBuilder } from 'newhooks/sportsbook/useOpenBetBuilder';
import { getSportIdByAlias } from 'store/selectors/new-sportsbook';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

const teamLogoSizeProps: { size: 'sm' | 'md'; sizeByPixels: number } = {
  size: isMobile() ? 'sm' : 'md',
  sizeByPixels: isMobile()
    ? SkeletonAvatarSize.SIZE_20
    : SkeletonAvatarSize.SIZE_24
};

export const DetailedTeamsInfo = ({
  game,
  showScores,
  sportAlias
}: {
  game: Game;
  showScores?: boolean;
  sportAlias: string;
}): ReactElement => {
  const configs = useSportsbookConfigs();

  const matchTrackerAvailable =
    Boolean(game.video_id) ||
    Boolean(game.video_id2) ||
    MATCH_TRACKER_AVAILABLE_SPORTS.includes(sportAlias);

  const currentSportId = useSelector(
    getSportIdByAlias(sportAlias, configs.type)
  );

  const betBuilderProps = {
    sportId: currentSportId,
    gameTitle: `${game.team1_name} - ${game.team2_name}`,
    fixtureId: game.sportcast_id,
    afterSignInActonName: `DetailedTeamsInfo_${game.id}_${AFTER_SIGN_IN_ACTION_TYPES.TOGGLE_BET_BUILDER_MODAL}`
  };

  const { openBetBuilder } = useOpenBetBuilder(betBuilderProps);

  const iconsConditions = {
    firstColumn: {
      betBuilder: !!game.sportcast_id && SpringConfigs.BET_BUILDER_SWITCHER,
      videoStream: game.hasVideoStream,
      matchTracker: matchTrackerAvailable
    },
    secondColumn: {
      statistics: SpringConfigs.STATISTICS_SWITCHER && game.is_stat_available,
      notification: SpringConfigs.PUSH_NOTIFICATIONS_ENABLED
    }
  };

  const firstColumnAvailable = Object.values(iconsConditions.firstColumn).some(
    el => el
  );

  const secondColumnAvailable = Object.values(
    iconsConditions.secondColumn
  ).some(el => el);

  const additionalScores = useMemo(() => {
    if (
      sportAlias === 'Cricket' &&
      game.stats &&
      game.info?.current_game_state
    ) {
      const currentSetScoreKey = `score_${game.info.current_game_state}`;

      return game.stats[currentSetScoreKey as keyof IGameStats];
    }
  }, [game.stats, game.info?.current_game_state, sportAlias]);

  return (
    <div className="detailedInfo__wrapper">
      <div className="detailedInfo__teamsInfo">
        <div
          className={cc([
            'detailedInfo__teamInfo__container',
            {
              'detailedInfo__teamInfo__container--mobile': isMobile()
            }
          ])}
        >
          <span className="detailedInfo__teamInfo__logo">
            <TeamLogo
              isHomeOrAway={ETeamNumber.HOME}
              isNotESport={checkIsNotESportType(game)}
              teamId={game.team1_id}
              {...teamLogoSizeProps}
            >
              <span className="iconWithTeamName">
                {String(game.team1_name).substr(0, 2).toUpperCase()}
              </span>
            </TeamLogo>
            <span className="detailedInfo__teamInfo__teamName">
              {game.team1_name}
            </span>
          </span>
          {showScores && (
            <span className="detailedInfo__score">
              {additionalScores?.team1_value || game.info?.score1}
            </span>
          )}
        </div>
        {!!game.team2_name && (
          <div
            className={cc([
              'detailedInfo__teamInfo__container',
              {
                'detailedInfo__teamInfo__container--mobile': isMobile()
              }
            ])}
          >
            <span className="detailedInfo__teamInfo__logo">
              <TeamLogo
                isHomeOrAway={ETeamNumber.AWAY}
                isNotESport={checkIsNotESportType(game)}
                teamId={game.team2_id}
                {...teamLogoSizeProps}
              >
                <span className="iconWithTeamName">
                  {String(game.team2_name).substr(0, 2).toUpperCase()}
                </span>
              </TeamLogo>
              <span className="detailedInfo__teamInfo__teamName">
                {game.team2_name}
              </span>
            </span>
            {showScores && (
              <span className="detailedInfo__score">
                {additionalScores?.team2_value || game.info?.score2}
              </span>
            )}
          </div>
        )}
      </div>
      <div
        className={cc([
          'detailedInfo__iconsGroup',
          {
            'detailedInfo__iconsGroup--mobile': isMobile()
          }
        ])}
      >
        {firstColumnAvailable && (
          <div className="detailedInfo__iconsGroup--column">
            {iconsConditions.firstColumn.betBuilder && (
              <EventAction
                iconContainerSize={EContainerIconSize.SMALL}
                icon={
                  <GlobalIcon
                    lib="generic"
                    name={
                      SpringConfigs.IS_AMERICAN_LAYOUT ? 'SGPIcon' : 'BBIcon'
                    }
                    theme="default"
                    size={EIconSize._16}
                  />
                }
                onClick={(e: SyntheticEvent) => {
                  e.stopPropagation();
                  openBetBuilder(e);
                }}
              />
            )}
            {iconsConditions.firstColumn.videoStream && (
              <VideoCamIcon
                size={EIconSize._16}
                iconContainerSize={EContainerIconSize.SMALL}
                marginDirection={null}
              />
            )}
            {matchTrackerAvailable && (
              <EventAction
                iconContainerSize={EContainerIconSize.SMALL}
                icon={
                  <GlobalIcon
                    lib="generic"
                    name="matchTrackerTab"
                    theme="default"
                    size={EIconSize._15}
                    className="matchTrackerIcon"
                  />
                }
              />
            )}
          </div>
        )}
        {secondColumnAvailable && (
          <ConditionalWrapper
            condition={!isMobile()}
            wrapper={children => (
              <div className="detailedInfo__iconsGroup--column">{children}</div>
            )}
          >
            <>
              {iconsConditions.secondColumn.statistics && (
                <MatchStatistic
                  size={EIconSize._16}
                  iconContainerSize={EContainerIconSize.SMALL}
                  matchId={game.id}
                  showStatisticInPopover={game.type !== 1}
                />
              )}
              {iconsConditions.secondColumn.notification && (
                <Suspense
                  fallback={
                    <span className="notificationIconSkeleton">
                      <Skeleton.Button shape="square" active />
                    </span>
                  }
                >
                  <NotificationIcon
                    game={game}
                    currentSport={sportAlias}
                    size={EIconSize._16}
                    iconContainerSize={EContainerIconSize.SMALL}
                  />
                </Suspense>
              )}
            </>
          </ConditionalWrapper>
        )}
      </div>
    </div>
  );
};
